import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import {
  AccountReceivableDebtor,
  AccountReceivableFinancialBySaleRequest,
  AccountReceivableListRequest,
  ListAccountReceivableBySaleRequest,
  ListAccountReceivableForGroupingRequest,
} from './AccountReceivable.types';
import accountReceivableServices, {
  accountReceivableIncludeTag,
  accountReceivableListGroupingById,
} from './AccountReceivable.services';
import notificationError from '@/utils/functions/notificationError';
import { notification } from 'antd';
import {
  AccountReceivableErrors,
  handleInstallmentAlreadyEdited,
} from './AccountRecivable.errors';
import { AccountPayableRangeDateRequest } from '../AccountPayable/AccountPayable.types';
import { AxiosError } from 'axios';
import { queryClient } from '@/Root';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { FINANCIAL_CONSULT_RECEIVABLE_LIST } from '../Consultation/consultation.hooks';

export const QUERY_ACCOUNT_RECEIVABLE = 'accountReceivable';
export const QUERY_ACCOUNT_RECEIVABLE_DETAIL = 'accountReceivable/detail';

export function useAccountReceivableList(
  params?: AccountReceivableListRequest,
  enabled?: boolean,
) {
  return useInfiniteQuery(
    [QUERY_ACCOUNT_RECEIVABLE, params],
    ({ pageParam }) =>
      accountReceivableServices.list({
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      }),
    {
      enabled: enabled,
      getNextPageParam: (lastPage) => lastPage.nextToken,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useAccountReceivableDetail(id?: number, enabled = true) {
  return useQuery(
    [QUERY_ACCOUNT_RECEIVABLE_DETAIL, id],
    () => accountReceivableServices.detail(id),
    {
      enabled: !!id && enabled,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useAccountReceivableCreate() {
  const queryChanel = useQueryChanel();

  return useMutation(accountReceivableServices.create, {
    onSuccess() {
      notification.success({
        message: 'Conta a receber cadastrada com sucesso!',
      });

      queryChanel({
        invalidateQueries: [[QUERY_ACCOUNT_RECEIVABLE]],
        resetQueries: [['dashboard-data'], [QUERY_ACCOUNT_RECEIVABLE]],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useAccountReceivableEdit() {
  const queryChanel = useQueryChanel();

  return useMutation(accountReceivableServices.edit, {
    onSuccess(_, data) {
      notification.success({
        message: 'Conta a receber editada com sucesso!',
      });

      queryChanel({
        invalidateQueries: [
          [QUERY_ACCOUNT_RECEIVABLE_DETAIL, data.accountReceivableId],
          [QUERY_ACCOUNT_RECEIVABLE],
        ],
        resetQueries: [['dashboard-data']],
      });
    },

    async onError(error: AxiosError, data) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) => AccountReceivableErrors[err] || err),
      });

      if (handleInstallmentAlreadyEdited(error)) {
        await queryClient.invalidateQueries([
          QUERY_ACCOUNT_RECEIVABLE_DETAIL,
          data.accountReceivableId,
        ]);
      }
    },
  });
}

export function useAccountReceivableSettleSingleInstallment() {
  const queryChanel = useQueryChanel();

  return useMutation(accountReceivableServices.settleSingleInstallment, {
    onSuccess(_, data) {
      notification.success({
        message: 'Parcela liquidada com sucesso!',
      });

      queryChanel({
        invalidateQueries: [
          [QUERY_ACCOUNT_RECEIVABLE],
          [QUERY_ACCOUNT_RECEIVABLE_DETAIL, data.id],
        ],
        resetQueries: [['dashboard-data']],
      });
    },

    async onError(error: AxiosError, data) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) => AccountReceivableErrors[err] || err),
      });

      if (handleInstallmentAlreadyEdited(error)) {
        await queryClient.invalidateQueries([
          QUERY_ACCOUNT_RECEIVABLE_DETAIL,
          data.id,
        ]);
      }
    },
  });
}

export function useAccountReceivableSettlePartiallyInstallment() {
  const queryChanel = useQueryChanel();

  return useMutation(accountReceivableServices.settlePartiallyInstallment, {
    onSuccess(_, data) {
      notification.success({
        message: 'Parcela parcialmente liquidada com sucesso!',
      });

      queryChanel({
        invalidateQueries: [
          [QUERY_ACCOUNT_RECEIVABLE],
          [QUERY_ACCOUNT_RECEIVABLE_DETAIL, data.id],
        ],
        resetQueries: [['dashboard-data']],
      });
    },

    async onError(error: AxiosError, data) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) => AccountReceivableErrors[err] || err),
      });

      if (handleInstallmentAlreadyEdited(error)) {
        await queryClient.invalidateQueries([
          QUERY_ACCOUNT_RECEIVABLE_DETAIL,
          data.id,
        ]);
      }
    },
  });
}

export function useAccountReceivableInstallmentCancel() {
  const queryChanel = useQueryChanel();

  return useMutation(accountReceivableServices.installmentCancel, {
    onSuccess() {
      notification.success({
        message: 'Parcela removida com sucesso!',
      });

      queryChanel({
        invalidateQueries: [[QUERY_ACCOUNT_RECEIVABLE]],
        resetQueries: [['dashboard-data']],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useAccountReceivableMultipleInstallment() {
  const queryChanel = useQueryChanel();

  return useMutation(accountReceivableServices.settleMultipleInstallment, {
    async onSuccess(_, { accountReceivableIds }) {
      notification.success({
        message: 'Parcelas liquidadas com sucesso!',
      });

      const uniqueAccountPayableIds: number[] = Array.from(
        new Set(accountReceivableIds),
      );

      await queryChanel({
        invalidateQueries: [
          [
            ...uniqueAccountPayableIds.map((accountPayableId) => [
              'installmentList-detail',
              accountPayableId,
            ]),
          ],
          [FINANCIAL_CONSULT_RECEIVABLE_LIST],
          [QUERY_ACCOUNT_RECEIVABLE],
        ],
        resetQueries: [['dashboard-data']],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useAccountReceivableSplitInstallment() {
  const queryChanel = useQueryChanel();

  return useMutation(accountReceivableServices.splitInstallment, {
    onSuccess(_, data) {
      notification.success({
        message: 'Parcelamento cadastrado com sucesso!',
      });

      queryChanel({
        invalidateQueries: [
          [QUERY_ACCOUNT_RECEIVABLE],
          [FINANCIAL_CONSULT_RECEIVABLE_LIST],
          [QUERY_ACCOUNT_RECEIVABLE_DETAIL, data.accountReceiveId],
          [`${QUERY_ACCOUNT_RECEIVABLE}/ListAccountReceivableBySale`],
        ],
        resetQueries: [['dashboard-data']],
      });
    },

    async onError(error: AxiosError, data) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) => AccountReceivableErrors[err] || err),
      });

      if (handleInstallmentAlreadyEdited(error)) {
        await queryClient.invalidateQueries([
          QUERY_ACCOUNT_RECEIVABLE_DETAIL,
          data.accountReceiveId,
        ]);
      }
    },
  });
}

export function useAccountReceivableReverseInstallment() {
  const queryChanel = useQueryChanel();

  return useMutation(accountReceivableServices.reverseInstallment, {
    onSuccess(_, data) {
      notification.success({
        message: 'Estorno feito com sucesso!',
      });

      queryChanel({
        invalidateQueries: [
          [QUERY_ACCOUNT_RECEIVABLE],
          [QUERY_ACCOUNT_RECEIVABLE_DETAIL, data.id],
        ],
        resetQueries: [['dashboard-data']],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useAccountReceivableRenegotiateInstallment() {
  const queryChanel = useQueryChanel();

  return useMutation(accountReceivableServices.renegotiateInstallment, {
    onSuccess(data) {
      notification.success({
        message: 'Renegociação feita com sucesso!',
      });

      queryChanel({
        invalidateQueries: [
          [QUERY_ACCOUNT_RECEIVABLE],
          [FINANCIAL_CONSULT_RECEIVABLE_LIST],
          [QUERY_ACCOUNT_RECEIVABLE_DETAIL, data.accountId],
          [`${QUERY_ACCOUNT_RECEIVABLE}/ListAccountReceivableBySale`],
        ],
        resetQueries: [['dashboard-data']],
      });
    },

    async onError(error: AxiosError, data) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) => AccountReceivableErrors[err] || err),
      });

      if (handleInstallmentAlreadyEdited(error)) {
        await queryClient.invalidateQueries([
          QUERY_ACCOUNT_RECEIVABLE_DETAIL,
          data.accountId,
        ]);
      }
    },
  });
}

export function useAccountReceivableListForGrouping(
  params?: ListAccountReceivableForGroupingRequest,
) {
  return useInfiniteQuery(
    [`${QUERY_ACCOUNT_RECEIVABLE}/listForGrouping`, params],
    ({ pageParam }) =>
      accountReceivableServices.listAccountReceivableForGrouping({
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      }),
    {
      enabled:
        !!params?.companyId &&
        !!params.debtor &&
        (params.debtor === AccountReceivableDebtor.Costumer
          ? !!params?.customerId
          : !!params?.insurerId),
      getNextPageParam: (lastPage) => lastPage.nextToken,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useAccountReceivableGenerateGrouping() {
  const queryChanel = useQueryChanel();

  return useMutation(
    accountReceivableServices.accountReceivableGenerateGrouping,
    {
      onSuccess() {
        notification.success({
          message: 'Agrupamento de parcelas efetuado com sucesso!',
        });

        queryChanel({
          invalidateQueries: [
            [QUERY_ACCOUNT_RECEIVABLE],
            [FINANCIAL_CONSULT_RECEIVABLE_LIST],
            [`${QUERY_ACCOUNT_RECEIVABLE}/listForGrouping`],
          ],
          resetQueries: [['dashboard-data']],
        });
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useAccountReceivableListBySale(
  params?: ListAccountReceivableBySaleRequest,
) {
  return useInfiniteQuery(
    [`${QUERY_ACCOUNT_RECEIVABLE}/ListAccountReceivableBySale`, params],
    ({ pageParam }) =>
      accountReceivableServices.listAccountReceivableBySale({
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      }),
    {
      enabled: !!params?.saleId,
      getNextPageParam: (lastPage) => lastPage.nextToken,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useGenerateAccountReceivableBySale() {
  const queryChanel = useQueryChanel();

  return useMutation(
    accountReceivableServices.accountReceivableGenerateBySale,
    {
      onSuccess(_, data) {
        queryChanel({
          invalidateQueries: [
            [`${QUERY_ACCOUNT_RECEIVABLE}/ListAccountReceivableBySale`],
            [`${QUERY_ACCOUNT_RECEIVABLE}/FinancialPendingBySale`],
            ['direct-sale', data.saleId],
          ],
          resetQueries: [['dashboard-data']],
        });
      },

      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useAccountReceivableFinancialPendingBySale(
  params: AccountReceivableFinancialBySaleRequest,
) {
  return useQuery(
    [`${QUERY_ACCOUNT_RECEIVABLE}/FinancialPendingBySale`, { ...params }],
    () =>
      accountReceivableServices.accountReceivableFinancialPendingBySale(params),
    {
      enabled: !!params.saleId,
      onError(err) {
        notificationError(err);
      },
    },
  );
}

export function useAccountReceivableGenerateFinancial() {
  const queryClient = useQueryClient();

  return useMutation(
    accountReceivableServices.accountReceivableGenerateFinancial,
    {
      async onSuccess(_, data) {
        await queryClient.invalidateQueries([
          'accountReceivable/financialPending',
          data.workOrderId,
        ]);

        await queryClient.invalidateQueries([
          'accountReceivable/financialSummary',
          data.workOrderId,
        ]);

        notification.success({ message: 'Financeiro gerado com sucesso!' });
      },
      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) => AccountReceivableErrors[err] || err),
        });
      },
    },
  );
}

export function useAccountReceivableFinancialPending(workorderId?: number) {
  return useQuery(
    ['accountReceivable/financialPending', workorderId],
    () =>
      accountReceivableServices.accountReceivableFinancialPending(workorderId),
    {
      enabled: !!workorderId,
      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) => AccountReceivableErrors[err] || err),
        });
      },
    },
  );
}

export function useAccountReceivableFinancialSummary(workorderId?: number) {
  return useQuery(
    ['accountReceivable/financialSummary', workorderId],
    () =>
      accountReceivableServices.accountReceivableFinancialSummary(workorderId),
    {
      enabled: !!workorderId,
      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) => AccountReceivableErrors[err] || err),
        });
      },
    },
  );
}

export function useAccountReceivableRangeDate(
  params?: AccountPayableRangeDateRequest,
) {
  return useQuery(
    ['range-date', params],
    () => accountReceivableServices.accountReceivableRangeDate(params),
    {
      onError(error) {
        notificationError(error);
      },
      enabled: !!params?.initialDate && !!params.finalDate,
    },
  );
}

export function useAccountReceivableReverseMultipleInstallments() {
  const queryChanel = useQueryChanel();

  return useMutation(accountReceivableServices.reverseMultipleInstallments, {
    onSuccess() {
      notification.success({
        message: 'Parcelas estornadas com sucesso',
      });

      queryChanel({
        invalidateQueries: [
          [QUERY_ACCOUNT_RECEIVABLE],
          [FINANCIAL_CONSULT_RECEIVABLE_LIST],
        ],
        resetQueries: [['dashboard-data']],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useAccountReceivableDeleteMultipleInstallments() {
  const queryChanel = useQueryChanel();

  return useMutation(accountReceivableServices.deleteMultipleInstallments, {
    onSuccess() {
      notification.success({
        message: 'Parcelas excluídas com sucesso',
      });

      queryChanel({
        invalidateQueries: [
          [QUERY_ACCOUNT_RECEIVABLE],
          [FINANCIAL_CONSULT_RECEIVABLE_LIST],
        ],
        resetQueries: [['dashboard-data']],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useAccountReceivableListGrouping(accountReceivableId?: number) {
  return useQuery(
    'AccountReceivable-ListGrouping',
    () => accountReceivableListGroupingById(accountReceivableId),
    { enabled: !!accountReceivableId },
  );
}

export function useAccountReceivableArchiveCreate() {
  return useMutation(accountReceivableServices.accountReceivableArchiveCreate, {
    async onSuccess(_, vars) {
      notification.success({ message: 'Anexo vinculado com sucesso!' });

      await queryClient.invalidateQueries([
        'accountReceivable/archives',
        { accountReceivableId: vars.accountReceivableId },
      ]);
      queryClient.removeQueries([
        'accountReceivable/archives',
        { accountReceivableId: vars.accountReceivableId },
      ]);
    },

    onError(err) {
      notificationError(err);
    },
  });
}

export function useAccountReceivableArchiveList(accountReceivableId?: number) {
  const cache = queryClient.getQueryData([
    'accountReceivable/archives',
    { accountReceivableId },
  ]);

  return useQuery(
    ['accountReceivable/archives', { accountReceivableId }],
    () =>
      accountReceivableServices.accountReceivableArchiveList(
        accountReceivableId,
      ),
    {
      enabled: !!accountReceivableId && !cache,
      onError(err) {
        notificationError(err);
      },
    },
  );
}

export function useAccountReceivableArchiveRemove() {
  return useMutation(accountReceivableServices.accountReceivableArchiveRemove, {
    async onSuccess(_, vars) {
      notification.success({ message: 'Anexo removido com sucesso!' });

      await queryClient.invalidateQueries([
        'accountReceivable/archives',
        { accountReceivableId: vars.accountReceivableId },
      ]);
      queryClient.removeQueries([
        'accountReceivable/archives',
        { accountReceivableId: vars.accountReceivableId },
      ]);
    },

    onError(err) {
      notificationError(err);
    },
  });
}

export function useAccountReceivableIncludeTag() {
  const queryChanel = useQueryChanel();

  return useMutation(accountReceivableIncludeTag, {
    onSuccess(_, data) {
      notification.success({
        message: `Etiqueta ${
          !!data.installmentTagId ? 'vinculada' : 'desvinculada'
        } à conta a receber`,
      });

      queryChanel({
        invalidateQueries: [
          [QUERY_ACCOUNT_RECEIVABLE_DETAIL, data.accountReceivableId],
        ],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useDirectSaleFinancialProcessing(
  saleId?: number,
  enabled = true,
) {
  return useQuery({
    queryKey: ['account-receivable/direct-sale/exist', saleId],
    queryFn: () =>
      accountReceivableServices.accountReceivableFinancialSaleExist(saleId),
    enabled: !!saleId && enabled,
    refetchInterval: 10000,
  });
}

export function useWorkOrderFinancialProcessing(workOrderId?: number) {
  return useQuery({
    queryKey: ['account-receivable/work-order/exist', workOrderId],
    queryFn: () =>
      accountReceivableServices.accountReceivableFinancialSaleExist(
        workOrderId,
      ),
  });
}
